<script setup lang="ts">
import {onMounted, ref, shallowRef} from "vue";
import {themeQuartz, type ColDef, type ICellRendererParams, type GridOptions, type GridApi, type GridReadyEvent} from "ag-grid-enterprise";
import {AgGridVue} from "ag-grid-vue3";
import {useConfirm} from "primevue/useconfirm";
import {useCharterChangesStore} from "@/charter/stores/charter-changes-store.ts";
import {columnTypes} from "@/app/ag-grid/ag-grid-column-types.ts";
import {dateFilterParams} from "@/app/ag-grid/ag-grid-filters.ts";
import {formatDate, formatDateTime} from "@/util/date-formatting.ts";
import type {Change, CharterWithChanges} from "@/api/client/models/index.ts";
import GridRouteFilter from "@/components/Allotment/GridRouteFilter.vue";

const charterChangesStore = useCharterChangesStore();
const showArchived = ref(false);
const gridApi = shallowRef<GridApi<CharterWithChanges> | null>(null);
const confirm = useConfirm();

const myTheme = themeQuartz.withParams({
  // rangeSelectionBorderStyle: 'none',
});

type CharterCellParams = ICellRendererParams<CharterWithChanges>;

const changeFieldAliases: Record<string, string[]> = {
  departureDate: ["date", "departure date"],
  carrier: ["aln", "airline"],
  flightNumber: ["flt", "flight", "flight number"],
  opSuffix: ["suffix", "op suffix", "op_suffix"],
  aircraftType: ["ac", "a/c", "aircraft", "aircraft type"],
  aircraftOwner: ["aircraft owner", "aircraft_owner", "owner"],
  seats: ["seat", "seats"],
  origin: ["from"],
  std: ["std"],
  destination: ["to"],
  sta: ["sta"],
  flightStatus: ["status", "flight status"],
  client: ["client detail"],
};

function normalizeChangeField(value: string | null | undefined): string {
  return value?.toLowerCase().replace(/[^a-z0-9]/g, "") ?? "";
}

function getFieldMatchKeys(field: string): string[] {
  return [field, ...(changeFieldAliases[field] ?? [])].map(normalizeChangeField);
}

function getOpenChangesForField(data: CharterWithChanges | undefined, field: string | undefined): Change[] {
  if (!data || !field) {
    return [];
  }

  const fieldKeys = getFieldMatchKeys(field);

  return data.changes?.filter((change) =>
    fieldKeys.includes(normalizeChangeField(change.field)) && change.isApproved !== true
  ) ?? [];
}

function colDefHasType(colDef: ColDef, type: string): boolean {
  return Array.isArray(colDef.type)
    ? colDef.type.includes(type)
    : colDef.type === type;
}

function formatTime(value: Date | string | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const timeMatch = String(value).match(/(?:^|T|\s)(\d{2}):(\d{2})/);

  if (timeMatch) {
    return `${timeMatch[1]}:${timeMatch[2]}`;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
}

function isTimeOnlyColumn(colDef: ColDef): boolean {
  return colDef.field === "std" || colDef.field === "sta";
}

function formatChangedValue(value: string | null | undefined, colDef: ColDef): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (isTimeOnlyColumn(colDef)) {
    return formatTime(value) || value;
  }

  if (colDefHasType(colDef, "dateColumn")) {
    return formatDate(value) || value;
  }

  if (colDefHasType(colDef, "dateTimeColumn")) {
    return formatDateTime(value) || value;
  }

  return value;
}

function formatCurrentValue(params: CharterCellParams): string {
  if (params.valueFormatted !== null && params.valueFormatted !== undefined) {
    return String(params.valueFormatted);
  }

  if (params.value === null || params.value === undefined) {
    return "";
  }

  return String(params.value);
}

function changedValueCellRenderer(params: CharterCellParams): HTMLElement {
  const field = params.colDef.field;
  const changes = getOpenChangesForField(params.data, field);

  if (!changes.length) {
    const value = document.createElement("span");
    value.textContent = formatCurrentValue(params);
    return value;
  }

  const container = document.createElement("span");
  container.className = "changed-value-cell";

  changes.forEach((change) => {
    const row = document.createElement("span");
    row.className = "changed-value-cell__row";

    const oldValue = document.createElement("span");
    oldValue.className = "changed-value-cell__old";
    oldValue.textContent = formatChangedValue(change.oldValue, params.colDef);

    const newValue = document.createElement("span");
    newValue.className = "changed-value-cell__new";
    newValue.textContent = formatChangedValue(change.newValue, params.colDef);

    row.append(oldValue, newValue);
    container.append(row);
  });

  return container;
}

const gridOptions: GridOptions = {
  domLayout: "normal",
  pagination: true,
  columnTypes: columnTypes,
  rowSelection: {
    mode: "multiRow",
    enableClickSelection: true,
  },
  selectionColumnDef: {
    pinned: "left",
  },
  defaultColDef: {
    resizable: false,
    cellRenderer: changedValueCellRenderer,
  },
  columnDefs: [
    {field: "departureDate", headerName: "Date", type: "dateColumn"},
    {field: "season", filter: true},
    {field: "snapshot", filter: true, type: "textColumn"},
    {field: "carrier", headerName: "Aln", type: "textColumn"},
    {field: "flightNumber", headerName: "Flt", type: "textColumn"},
    {field: "opSuffix", headerName: "Suffix", type: "textColumn"},
    {field: "aircraftType", headerName: "Ac", type: "textColumn"},
    {field: "aircraftOwner", headerName: "Aircraft owner", type: "textColumn"},
    {field: "seats", headerName: "Seats", type: "numberColumn"},
    {field: "origin", headerName: "From", type: "textColumn"},
    {field: "std", filterParams: dateFilterParams, type: "dateTimeColumn", valueFormatter: (params) => formatTime(params.value)},
    {field: "destination", headerName: "To", type: "textColumn"},
    {field: "sta", filterParams: dateFilterParams, type: "dateTimeColumn", valueFormatter: (params) => formatTime(params.value)},
    {field: "flightStatus", headerName: "Status", filter: true},
    {field: "client", headerName: "Client detail", filter: true},
    {field: "stretch", filter: GridRouteFilter},
    {field: "route", filter: GridRouteFilter},
    {field: "version", filter: true},
  ],
};

onMounted(async () => {
  await refreshData();
});

async function refreshData() {
  await charterChangesStore.fetchChartersWithChanges(showArchived.value);
}

function onGridReady(params: GridReadyEvent<CharterWithChanges>) {
  gridApi.value = params.api;
}

function ackChanges() {
  const charterIds = gridApi.value?.getSelectedRows()
    .map((charter) => charter.id)
    .filter((id): id is number => id !== null && id !== undefined) ?? [];

  if (!charterIds.length) {
    return;
  }

  showConfirm("Are you sure you wish to acknowledge these changes?", "Confirm changes", () =>
    charterChangesStore.acknowledgeChanges(charterIds)
      .then(refreshData)
  );
}

function showConfirm(
  message: string,
  title: string,
  onAccept: () => Promise<void>,
): void {
  confirm.require({
    message: message,
    header: title,
    // rejectProps: {
    //     label: rejectLabel,
    //     severity: 'secondary',
    //     outlined: true
    // },
    // acceptProps: {
    //     label: acceptLabel
    // },
    accept: onAccept,
  });
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h1>Updated flights</h1>
      <div class="flex items-center gap-2">
        <p-input-switch v-model="showArchived" @change="refreshData" :disabled="charterChangesStore.isLoading" />
        <label class="mr-2">Show archived</label>
        <p-button
          severity="secondary"
          link
          @click="refreshData"
          :loading="charterChangesStore.isLoading"
        >
          <span class="material-icons" title="refresh">refresh</span>
        </p-button>
        <p-button
          severity="secondary"
          link
          @click="ackChanges"
        >
          <span class="material-icons" title="Acknowledge changes">save</span>
        </p-button>
      </div>
    </div>
    <div v-if="charterChangesStore.hasError" class="p-message p-message-error">
      <span class="p-message-text">
        {{ charterChangesStore.error }}
      </span>
      <button class="p-message-close p-link" @click="charterChangesStore.clearError">
        <span class="material-icons">close</span>
      </button>
    </div>
    <AgGridVue
      :gridOptions="gridOptions"
      :loading="charterChangesStore.isLoading"
      :rowData="charterChangesStore.chartersWithChanges"
      :theme="myTheme"
      @grid-ready="onGridReady"
      style="width: 100%; height: 100%"
    />
  </div>
</template>

<style scoped lang="scss">
:deep(.changed-value-cell) {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  max-width: 100%;
  vertical-align: middle;
}

:deep(.changed-value-cell__row) {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
}

:deep(.changed-value-cell__old),
:deep(.changed-value-cell__new) {
  overflow-wrap: anywhere;
}

:deep(.changed-value-cell__old) {
  color: #d32f2f;
  font-weight: 600;
  text-decoration: line-through;
}
</style>
